import type PptxGenJS from 'pptxgenjs'
import type { PlanData, LayoutConfig } from './dealCalculations'
import {
  defaultLayoutConfig,
  layoutPercentToFraction,
  DEFAULT_SUPPORT_HOURS_Y,
  DEFAULT_NEGOTIATION_Y,
} from './dealCalculations'
import { enrichExportLayoutConfig } from './planCatalog'
import { getSlideProjectTypeLabel } from './slideProjectTypeLabel'
import { formatSupportHoursSlideLabel, formatSupportTierSlideLine } from './planSuportePricing'
import { formatNegotiationDeadlineSlideLabel } from './negotiationDeadline'
import {
  groupServicesByCategory,
  calculateTCV,
} from './dealCalculations'
import { buildPptxCategoryBlocks, renderPptxCategoryBlocks } from './pptxSlideCategoryLayout'
import {
  formatCategoryDisplayTitle,
  formatServiceLineLabel,
  normalizeTextEncoding,
} from './text-normalization'
import { formatCurrency } from './format-currency'
import { formatSlideDescriptionBody } from '@/components/Deals/ProposalStudio/slidePreviewTheme'
import { getTemplateType } from './pptxTemplateType'

const GREEN_ACCENT = '40C351'
const ROCKET_DARK = '070712'
const ROCKET_CARD = '111326'
const ROCKET_MUTED = 'C8CADE'
const ROCKET_PINK = 'FF2F86'
const ROCKET_PURPLE = '7E22CE'
const WHITE = 'FFFFFF'
/** Bloco 6 — valor principal no PPTX (polegadas, pt). */
const PPTX_PRICE_FONT_MAIN = 22
const PPTX_PRICE_FONT_COMPARE = 12

export type InvestmentSlideRenderOptions = {
  /** Oculta preço, bônus, parcelas e fidelidade (ex.: slides intermediários de combo). */
  hidePricing?: boolean
}

/** Adiciona um slide de investimento ao deck (mesmo layout do export de plano único). */
export function appendInvestmentSlide(
  pptx: PptxGenJS,
  plan: PlanData,
  layoutConfig: LayoutConfig,
  options: InvestmentSlideRenderOptions = {},
): void {
  const hidePricing = options.hidePricing === true
  const slide = pptx.addSlide()
  const cfg = enrichExportLayoutConfig(layoutConfig, plan.nome)
  const slideW = 13.33
  const slideH = 7.5

  const templateType = getTemplateType(plan)
  const leftBlockWidthVal = (cfg.leftBlockWidth > 1 ? cfg.leftBlockWidth / 100 : cfg.leftBlockWidth) || 0.35
  const leftW = slideW * leftBlockWidthVal
  const rightX = leftW
  const rightW = slideW - leftW

  slide.background = { color: ROCKET_DARK }
  slide.addShape(pptx.ShapeType.rect, {
    x: 0, y: 0, w: slideW, h: slideH,
    fill: { color: ROCKET_DARK },
    line: { color: ROCKET_DARK },
  })
  slide.addShape(pptx.ShapeType.ellipse, {
    x: slideW * 0.72, y: -0.35, w: 3.9, h: 3.9,
    fill: { color: ROCKET_PINK, transparency: 78 },
    line: { color: ROCKET_PINK, transparency: 100 },
  })
  slide.addShape(pptx.ShapeType.ellipse, {
    x: slideW * 0.36, y: 2.2, w: 4.6, h: 4.6,
    fill: { color: ROCKET_PURPLE, transparency: 86 },
    line: { color: ROCKET_PURPLE, transparency: 100 },
  })
  slide.addShape(pptx.ShapeType.line, {
    x: 0.42, y: 0.54, w: 0, h: 0.42,
    line: { color: ROCKET_PINK, width: 3 },
  })
  slide.addText('ROCKET SEARCH', {
    x: slideW - 2.25, y: 0.38, w: 1.8, h: 0.28,
    fontSize: 7, color: ROCKET_PINK, bold: true, fontFace: 'Arial',
    align: 'right', lang: 'pt-BR',
  })

  const titleFontSize = 28
  const yFromLayout = (value: number | undefined, fallbackPercent: number) =>
    layoutPercentToFraction(value, fallbackPercent) * slideH

  const titleY = yFromLayout(cfg.titleY, defaultLayoutConfig.titleY)
  slide.addText([
    { text: 'Plano Rocket Search', options: { color: WHITE, fontSize: titleFontSize, bold: true, lang: 'pt-BR' } },
    { text: '.', options: { color: ROCKET_PINK, fontSize: titleFontSize, bold: true, lang: 'pt-BR' } },
  ], {
    x: 0.4, y: titleY, w: leftW - 0.8, h: 0.8,
    fontFace: 'Arial',
    lang: 'pt-BR',
  })

  const projectTypeLabel = getSlideProjectTypeLabel(plan)
  if (projectTypeLabel) {
    const projectTypeY = yFromLayout(cfg.projectTypeY, defaultLayoutConfig.projectTypeY)
    slide.addText(normalizeTextEncoding(projectTypeLabel).toUpperCase(), {
      x: 0.4, y: projectTypeY, w: leftW - 0.8, h: 0.4,
      fontSize: 14, color: ROCKET_PINK, bold: true, fontFace: 'Arial', lang: 'pt-BR',
    })
  }

  const descriptionY = yFromLayout(cfg.descriptionY, defaultLayoutConfig.descriptionY)
  const planDescricao = formatSlideDescriptionBody(normalizeTextEncoding(plan.descricao || ''))
  if (planDescricao) {
    slide.addText(planDescricao, {
      x: 0.4, y: descriptionY, w: leftW - 0.8, h: 1.35,
      fontSize: 11, color: ROCKET_MUTED, fontFace: 'Arial', valign: 'top', lineSpacingMultiple: 1.15,
      lang: 'pt-BR',
    })
  }

  const supportTierLines =
    plan.supportTiers && plan.supportTiers.length >= 2
      ? plan.supportTiers
          .map((t) => formatSupportTierSlideLine(t.supportHours, t.monthlyValue))
          .filter(Boolean)
      : []
  const multiSupportTiers = supportTierLines.length >= 2

  const supportHoursLabel = formatSupportHoursSlideLabel(plan.supportHours ?? 0)
  if (supportHoursLabel && !multiSupportTiers) {
    const supportHoursY = yFromLayout(cfg.supportHoursY, DEFAULT_SUPPORT_HOURS_Y)
    slide.addText(supportHoursLabel, {
      x: 0.4,
      y: supportHoursY,
      w: leftW - 0.8,
      h: 0.35,
      fontSize: 11,
      color: ROCKET_PINK,
      bold: true,
      fontFace: 'Arial',
      valign: 'top',
      lang: 'pt-BR',
    })
  }

  if (multiSupportTiers) {
    const supportHoursY = yFromLayout(cfg.supportHoursY, DEFAULT_SUPPORT_HOURS_Y)
    const tierText = supportTierLines.map((line) => `• ${line}`).join('\n')
    slide.addText(tierText, {
      x: 0.4,
      y: supportHoursY,
      w: leftW - 0.8,
      h: 2.2,
      fontSize: 9,
      color: WHITE,
      bold: true,
      fontFace: 'Arial',
      valign: 'top',
      lineSpacingMultiple: 1.15,
      lang: 'pt-BR',
    })
  }

  if (templateType === 'ecommerce') {
    const ecomY = descriptionY + 1.3
    slide.addText('Implantação em 60 a 90 dias incluindo configuração de produtos, meios de pagamento, frete e integrações.', {
      x: 0.4, y: ecomY, w: leftW - 0.8, h: 0.8,
      fontSize: 10, color: 'AAAAAA', fontFace: 'Arial', valign: 'top',
    })
  }

  if (!hidePricing) {
    if (plan.showBonus && plan.bonusText) {
      const bonusY = yFromLayout(cfg.bonusY, defaultLayoutConfig.bonusY)
      slide.addShape(pptx.ShapeType.roundRect, {
        x: 0.35, y: bonusY, w: leftW - 0.7, h: 0.45,
        fill: { color: ROCKET_PINK, transparency: 88 },
        rectRadius: 0.1,
        line: { color: ROCKET_PINK, width: 1.5 },
      })
      const bonusLabel = normalizeTextEncoding(plan.bonusText || '')
      const bonusDisplay = /^b[oô]nus/i.test(bonusLabel) ? bonusLabel : `BÔNUS: ${bonusLabel}`
      slide.addText(`★ ${bonusDisplay}`, {
        x: 0.4, y: bonusY, w: leftW - 0.8, h: 0.45,
        fontSize: 10, color: ROCKET_PINK, bold: true, fontFace: 'Arial', align: 'center', valign: 'middle',
      })
    }

    const tcv = calculateTCV(plan)
    const monthlyValue =
      plan.pricing.valorMensal ??
      (plan.pricing as { installmentValue?: number }).installmentValue ??
      plan.pricing.valor
    const priceStr = plan.pricing.recorrente
      ? `${formatCurrency(Number(monthlyValue) || 0)}/mês`
      : formatCurrency(Number.isFinite(tcv) ? tcv : Number(monthlyValue) || 0)
    const priceY = yFromLayout(cfg.priceY, defaultLayoutConfig.priceY)
    const compareFrom = plan.priceCompareFrom
    const showDePor =
      !multiSupportTiers &&
      compareFrom !== undefined &&
      compareFrom > Number(monthlyValue) + 0.009

    if (!multiSupportTiers) {
      if (showDePor) {
        const deStr = `${formatCurrency(compareFrom)}/mês`
        slide.addText(`De ${deStr}`, {
          x: 0.4, y: priceY - 0.4, w: leftW - 0.8, h: 0.32,
          fontSize: PPTX_PRICE_FONT_COMPARE, color: 'FF0000', fontFace: 'Arial', strike: true,
        })
        slide.addText(`Por ${priceStr}`, {
          x: 0.4, y: priceY, w: leftW - 0.8, h: 0.6,
          fontSize: PPTX_PRICE_FONT_MAIN, color: ROCKET_PINK, bold: true, fontFace: 'Arial',
        })
      } else {
        slide.addText('INVESTIMENTO MENSAL', {
          x: 0.4, y: priceY - 0.28, w: leftW - 0.8, h: 0.2,
          fontSize: 7, color: ROCKET_PINK, bold: true, fontFace: 'Arial',
        })
        slide.addText(priceStr, {
          x: 0.4, y: priceY, w: leftW - 0.8, h: 0.6,
          fontSize: PPTX_PRICE_FONT_MAIN, color: ROCKET_PINK, bold: true, fontFace: 'Arial',
        })
      }
    }

    if (plan.paymentDetails) {
      const paymentY = yFromLayout(cfg.paymentY, defaultLayoutConfig.paymentY)
      slide.addText(plan.paymentDetails, {
        x: 0.4, y: paymentY, w: leftW - 0.8, h: 0.4,
        fontSize: 10, color: ROCKET_MUTED, fontFace: 'Arial',
      })
    }

    if (plan.loyaltyDuration) {
      const loyaltyY = yFromLayout(cfg.loyaltyY, defaultLayoutConfig.loyaltyY)
      slide.addText(plan.loyaltyDuration, {
        x: 0.4, y: loyaltyY, w: leftW - 0.8, h: 0.3,
        fontSize: 10, color: 'A8ABC2', fontFace: 'Arial',
      })
    }

    const negotiationLabel = formatNegotiationDeadlineSlideLabel(plan.negotiationValidUntil)
    if (negotiationLabel) {
      const negotiationY = yFromLayout(cfg.negotiationY, DEFAULT_NEGOTIATION_Y)
      slide.addText(negotiationLabel, {
        x: 0.4,
        y: negotiationY,
        w: leftW - 0.8,
        h: 0.28,
        fontSize: 9,
        color: '8E92AD',
        fontFace: 'Arial',
        lang: 'pt-BR',
      })
    }
  }

  const planTitleY = yFromLayout(cfg.planTitleY, defaultLayoutConfig.planTitleY)
  slide.addText(plan.nome, {
    x: rightX + 0.4, y: planTitleY, w: rightW - 0.8, h: 0.5,
    fontSize: 20, color: WHITE, bold: true, fontFace: 'Arial', lang: 'pt-BR',
  })

  if (templateType === 'cro') {
    const groups = groupServicesByCategory(plan.servicos)
    const croCategories = Array.from(groups.entries()).map(([nome, items]) => ({
      nome: formatCategoryDisplayTitle(nome),
      items: items.map((s) => formatServiceLineLabel(s.nome, s.quantidade)),
    }))
    const croStartY = yFromLayout(cfg.serviceBlockStartY, defaultLayoutConfig.serviceBlockStartY)
    croCategories.forEach((cat, idx) => {
      const col = idx % 2
      const row = Math.floor(idx / 2)
      const cx = col === 0 ? rightX + 0.4 : rightX + rightW / 2 + 0.2
      const cw = rightW / 2 - 0.6
      const cy = croStartY + row * 2.8

      slide.addText(cat.nome, {
        x: cx, y: cy, w: cw, h: 0.35,
        fontSize: 11, color: WHITE, bold: true, fontFace: 'Arial',
      })
      cat.items.forEach((item, i) => {
        slide.addText([
          { text: '✓  ', options: { color: GREEN_ACCENT, fontSize: 9, bold: true } },
          { text: item, options: { color: ROCKET_MUTED, fontSize: 9 } },
        ], {
          x: cx + 0.15, y: cy + 0.35 + i * 0.26, w: cw - 0.15, h: 0.26,
          fontFace: 'Arial',
        })
      })
    })
  } else if (templateType === 'layout-personalizado') {
    const groups = groupServicesByCategory(plan.servicos)
    const layoutCategories = Array.from(groups.entries()).map(([nome, items]) => ({
      nome: formatCategoryDisplayTitle(nome),
      items: items.map((s) => formatServiceLineLabel(s.nome, s.quantidade)),
    }))
    const layoutStartY = yFromLayout(cfg.serviceBlockStartY, defaultLayoutConfig.serviceBlockStartY)
    layoutCategories.forEach((cat, idx) => {
      const col = idx % 2
      const row = Math.floor(idx / 2)
      const cx = col === 0 ? rightX + 0.4 : rightX + rightW / 2 + 0.2
      const cw = rightW / 2 - 0.6
      const cy = layoutStartY + row * 2.5

      slide.addText(cat.nome, {
        x: cx, y: cy, w: cw, h: 0.35,
        fontSize: 11, color: WHITE, bold: true, fontFace: 'Arial',
      })
      cat.items.forEach((item, i) => {
        slide.addText([
          { text: '✓  ', options: { color: GREEN_ACCENT, fontSize: 9, bold: true } },
          { text: item, options: { color: ROCKET_MUTED, fontSize: 9 } },
        ], {
          x: cx + 0.15, y: cy + 0.35 + i * 0.26, w: cw - 0.15, h: 0.26,
          fontFace: 'Arial',
        })
      })
    })
  } else {
    const leftBlockWidthPercent = leftBlockWidthVal * 100
    const blocks = buildPptxCategoryBlocks(
      cfg,
      plan.servicos,
      slideW,
      slideH,
      leftBlockWidthPercent,
    )
    renderPptxCategoryBlocks(slide, blocks, {
      totalItems: plan.servicos.length,
      categoryColor: WHITE,
      itemColor: ROCKET_MUTED,
      cardFill: ROCKET_CARD,
      cardLine: '2A2E45',
    })
  }
}
